'use client'

import React, { useEffect } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayCollectionIndexEntry, HolidayPackagesIndexEntry } from '@/src/types/holidays'
import useHolidaysGtmEvent from '@/src/hooks/analytics/gtm/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import useHolidayAppMode from '@/src/hooks/holidays/useHolidayAppMode'
import { HOLIDAY_ACTION } from '@/src/utils/analytics/holidays/holidayEvents'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import HolidayIndexPage from './indexRootLoader'

interface HolidayIndexRootLoaderProps {
    entries: HolidayCollectionIndexEntry[]
    packages: HolidayPackagesIndexEntry[]
    isMobile: boolean
    domainCountry: string
    showVisaTab: boolean
    showFlightsHotelsTab: boolean
}

const HolidayIndexRootLoader: React.FC<HolidayIndexRootLoaderProps> = ({
    entries,
    packages,
    isMobile,
    domainCountry,
    showVisaTab,
    showFlightsHotelsTab
}) => {
    const { pushHolidayHomePageViewEvent } = useHolidaysGtmEvent()
    const { triggerHolidayPageLoadEvent } = useClevertapForHolidays()
    const t = useTranslations('holidaysPage')
    const isApp = useHolidayAppMode()

    // Once per load, to both sinks: GTM names the page in the event, CleverTap in `action_name`.
    useEffect(() => {
        pushHolidayHomePageViewEvent()
        // No provider on this route, so the market the page was served for is passed in.
        triggerHolidayPageLoadEvent(HOLIDAY_ACTION.INDEX_VIEWED, {
            market: domainCountry || 'NA',
            collection_count: entries.length
        })
    }, [])

    return (
        <ErrorBoundaryWrapper identifier="holidays-index">
            <HolidayIndexPage
                entries={entries}
                packages={packages}
                heading={t('indexHeroHeading')}
                tagline={t('indexHeroTagline')}
                subheading={t('indexHeroSubheading')}
                showVisaTab={showVisaTab}
                showFlightsHotelsTab={showFlightsHotelsTab}
                isMobile={isMobile}
                domainCountry={domainCountry}
                isApp={isApp}
                appTitle={t('indexHeading')}
            />
        </ErrorBoundaryWrapper>
    )
}

export default HolidayIndexRootLoader
